import { cn } from "@/lib/utils/cn";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";
import * as React from "react";
import { Button } from "../button";

const DialogRoot = ({ ...props }: React.ComponentProps<typeof BaseDialog.Root>) => {
  return <BaseDialog.Root {...props} />;
};

const DialogTrigger = ({ ...props }: React.ComponentProps<typeof BaseDialog.Trigger>) => {
  return <BaseDialog.Trigger data-slot="dialog-trigger" {...props} />;
};

const DialogClose = ({ ...props }: React.ComponentProps<typeof BaseDialog.Close>) => {
  return <BaseDialog.Close data-slot="dialog-close" {...props} />;
};

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseDialog.Backdrop>) => {
  return (
    <BaseDialog.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/30 transition-opacity duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
        className,
      )}
      {...props}
    />
  );
};

const dialogContentVariants = cva(
  "bg-background fixed top-[50%] left-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg max-h-[calc(100vh-2rem)] overflow-y-auto transition-[opacity,transform,translate,scale] duration-200 data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
  {
    variants: {
      size: {
        default: "max-w-[calc(100%-2rem)] sm:max-w-lg",
        lg: "max-w-[calc(100%-2rem)] sm:max-w-2xl",
        xl: "max-w-[calc(100%-2rem)] sm:max-w-4xl",
        "2xl": "max-w-[calc(100%-2rem)] sm:max-w-5xl",
        tall: "max-w-[calc(100%-2rem)] sm:max-w-2xl h-[calc(100vh-4rem)]",
        full: "max-w-[calc(100%-2rem)] sm:max-w-[calc(100%-4rem)] h-[calc(100vh-4rem)]",
        screen:
          "!top-0 !left-0 !right-0 !bottom-0 !translate-x-0 !translate-y-0 !inset-0 !w-screen !h-screen !max-w-none !max-h-none !min-w-full !min-h-full !p-0 !m-0 rounded-none border-none shadow-none",
      },
      variant: {
        default: "border-border",
        destructive: "border-destructive",
        warning: "border-warning",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  },
);

const DialogContent = ({
  children,
  className,
  size,
  variant,
  hideCloseButton = false,
  ...props
}:
  & React.ComponentProps<typeof BaseDialog.Popup>
  & VariantProps<typeof dialogContentVariants>
  & {
    readonly hideCloseButton?: boolean;
  }) =>
{
  return (
    <BaseDialog.Portal>
      <DialogOverlay />
      <BaseDialog.Popup
        data-slot="dialog-content"
        className={cn(dialogContentVariants({ size, variant, className }))}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <BaseDialog.Close className="absolute top-4 right-4 cursor-pointer rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Close</span>
          </BaseDialog.Close>
        )}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
};

const DialogHeader = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
};

const DialogFooter = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
};

const DialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseDialog.Title>) => {
  return (
    <BaseDialog.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
};

const DialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseDialog.Description>) => {
  return (
    <BaseDialog.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  );
};

const CancelButton = (
  props: Omit<React.ComponentProps<typeof Button>, "children" | "variant"> & {
    children?: React.ReactNode;
  },
) => {
  return (
    <DialogClose
      render={
        <Button variant="secondary" {...props}>
          {props.children ?? "Cancel"}
        </Button>
      }
    />
  );
};

export const Dialog = Object.assign(DialogRoot, {
  Trigger: DialogTrigger,
  Close: DialogClose,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Header: DialogHeader,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
  CancelButton,
});
